import React, { useState } from "react";
import DiscountIcon from "@mui/icons-material/Discount";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

/* ---------------- Types ---------------- */

interface CouponDiscountDetails {
  discountAmount: number;
  orderBefore: number;
  orderAfter: number;
  savingsPercentage: string;
}

interface Coupon {
  code: string;
  type: string;
  value: string;
}

interface CouponSectionProps {
  isRTL: boolean;
  textAlignClass: string;
  currentCoupon: Coupon | null;
  couponDiscount: CouponDiscountDetails | null;
  onApplyCoupon: (code: string) => Promise<unknown>;
  onRemoveCoupon: () => Promise<void>;
  t: (key: string) => string;
  isCitiesSelected: boolean;
}


/* ---------------- Component ---------------- */

const CouponSection: React.FC<CouponSectionProps> = ({
  isRTL,
  textAlignClass,
  currentCoupon,
  couponDiscount,
  onApplyCoupon,
  onRemoveCoupon,
  t,
  isCitiesSelected,
}) => {
  const [couponCode, setCouponCode] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /* ---------------- Handlers ---------------- */

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!couponCode.trim()) {
      setError(t("cart.enterCouponCode"));
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      await onApplyCoupon(couponCode.trim());
    } catch {
      setError(t("cart.couponError"));
    } finally {
      setIsLoading(false);
    }
  };

  const handleRemove = async () => {
    setIsLoading(true);
    setError(null);

    try {
      await onRemoveCoupon();
      setCouponCode("");
    } catch {
      setError(t("cart.removeCouponError"));
    } finally {
      setIsLoading(false);
    }
  };

  /* ---------------- UI ---------------- */

  return (
    <div className="bg-gradient-to-r from-purple-50 to-white rounded-xl shadow-sm p-6 mt-6 border border-purple-100">
      {/* Header */}
      <div
        className={`flex items-center justify-between mb-4 ${
          isRTL ? "flex-row-reverse" : ""
        }`}
      >
        <div className={`flex items-center gap-3 ${textAlignClass}`}>
          <div className="bg-purple-100 p-2 rounded-lg">
            <DiscountIcon className="text-purple-600" />
          </div>
          <div>
            <h3 className="font-bold text-lg text-gray-800">
              {t("cart.haveCoupon")}
            </h3>
            <p className="text-sm text-gray-600">
              {t("cart.enterCouponBelow")}
            </p>
          </div>
        </div>

        {currentCoupon && (
          <div className="flex items-center gap-2 bg-green-50 px-3 py-1 rounded-full">
            <CheckCircleIcon className="text-green-500 text-sm" />
            <span className="text-green-700 text-sm font-medium">
              {t("cart.couponApplied")}
            </span>
          </div>
        )}
      </div>

      {/* No Coupon Applied */}
      {!currentCoupon ? (
        <form
          onSubmit={handleSubmit}
          className={`space-y-3 ${isRTL ? "text-right" : "text-left"}`}
        >
          <div className="flex gap-2">
            <input
              type="text"
              value={couponCode}
              onChange={(e) =>
                setCouponCode(e.target.value.toUpperCase())
              }
              placeholder={t("cart.enterCouponCode")}
              dir="ltr"
              disabled={!isCitiesSelected}
              className={`flex-1 border rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-purple-500
                ${
                  error ? "border-red-300" : "border-gray-300"
                }`}
            />

            <button
              type="submit"
              disabled={isLoading || !isCitiesSelected}
              className={`bg-purple-600 text-white px-6 py-3 rounded-lg font-medium transition-all
                ${
                  isLoading || !isCitiesSelected
                    ? "opacity-50 cursor-not-allowed"
                    : "hover:bg-purple-700 hover:shadow-md"
                }`}
            >
              {isLoading ? t("cart.applying") : t("cart.applyCoupon")}
            </button>
          </div>

          {error && (
            <p className="text-red-600 text-sm flex items-center gap-1">
              <CloseIcon fontSize="small" />
              {error}
            </p>
          )}

          {!isCitiesSelected && (
            <p className="text-amber-600 text-sm">
              ⚠️ {t("cart.selectCitiesFirst")}
            </p>
          )}
        </form>
      ) : (
        /* Coupon Applied */
        <div className={`space-y-3 ${isRTL ? "text-right" : "text-left"}`}>
          <div className="flex items-center justify-between bg-green-50 p-4 rounded-lg">
            <div className="flex items-center gap-3">
              <div className="bg-green-100 p-2 rounded-lg">
                <CheckCircleIcon className="text-green-600" />
              </div>
              <div>
                <h4 className="font-bold text-green-800">
                  {currentCoupon.code}
                </h4>
                <p className="text-sm text-green-600">
                  {t("cart.couponActive")}
                </p>
              </div>
            </div>

            <div>
              <div className="text-2xl font-bold text-green-700">
                -{couponDiscount?.discountAmount.toFixed(2)}{" "}
                {t("currency.egp")}
              </div>
              <div className="text-sm text-green-600">
                {t("cart.savings")}:{" "}
                {couponDiscount?.savingsPercentage}%
              </div>
            </div>
          </div>

          <button
            onClick={handleRemove}
            disabled={isLoading}
            className="text-red-600 hover:text-red-700 text-sm font-medium flex items-center gap-1"
          >
            <CloseIcon fontSize="small" />
            {t("cart.removeCoupon")}
          </button>
        </div>
      )}
    </div>
  );
};

export default CouponSection;
